import { useEffect, useState } from 'react'
import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'
import { useAuthStore } from '@/store'

/**
 * App-level роль «супер-админ» (`management_company_access.is_super_admin`).
 * Отвечает за раздел `/management/users`: справочник пользователей, журнал попыток
 * авторизации, активность сотрудников.
 *
 * Сознательно НЕ кешируется в localStorage (в отличие от `getAccessLevel`): снятый признак
 * должен переставать действовать со следующей загрузки, а не жить в кеше устройства.
 * Настоящий гейт — серверный (`public.is_super_admin()` внутри RPC и RLS-политик);
 * этот хук лишь прячет то, что всё равно не отдадут.
 */
export function useSuperAdmin(): { isSuperAdmin: boolean | undefined } {
  const user = useAuthStore(s => s.user)
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean | undefined>(undefined)

  useEffect(() => {
    let cancelled = false

    if (!user?.id) {
      setIsSuperAdmin(false)
      return
    }

    setIsSuperAdmin(undefined)
    void (async () => {
      try {
        const { data, error } = (await withAuthRecovery(() =>
          (getSupabase() as any).rpc('is_super_admin')
        )) as { data: boolean | null; error: any }
        if (cancelled) return
        // Функции нет (миграция не применена) — считаем, что роли ни у кого нет.
        setIsSuperAdmin(!error && data === true)
      } catch {
        if (!cancelled) setIsSuperAdmin(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [user?.id])

  return { isSuperAdmin }
}
